<template>
  <div class="error-analysis-content">
    <!-- Loading Indicator -->
    <div v-if="isLoading" class="notification is-info is-light">
      <span class="icon">
        <i class="fas fa-spinner fa-spin"></i>
      </span>
      <span>Loading error analysis data...</span>
    </div>

    <!-- Error Analysis Content -->
    <div v-else>
      <!-- Summary Statistics -->
      <div class="columns is-multiline mb-5">
        <div class="column is-2">
          <div class="card has-background-danger">
            <div class="card-content has-text-centered">
              <p class="title is-4 has-text-white">{{ formatNumber(summaryStats.total_failed_invoices) }}</p>
              <p class="subtitle is-6 has-text-white">Total Failed</p>
            </div>
          </div>
        </div>
        <div class="column is-2">
          <div class="card has-background-warning">
            <div class="card-content has-text-centered">
              <p class="title is-4 has-text-white">{{ summaryStats.failure_rate_percentage }}%</p>
              <p class="subtitle is-6 has-text-white">Failure Rate</p>
            </div>
          </div>
        </div>
        <div class="column is-2">
          <div class="card" style="background-color: #0a3954;">
            <div class="card-content has-text-centered">
              <p class="title is-4 has-text-white">{{ summaryStats.unique_error_types }}</p>
              <p class="subtitle is-6 has-text-white">Error Types</p>
            </div>
          </div>
        </div>
        <div class="column is-2">
          <div class="card" style="background-color: #0a1821;">
            <div class="card-content has-text-centered">
              <p class="title is-4 has-text-white">{{ summaryStats.unique_clients_affected }}</p>
              <p class="subtitle is-6 has-text-white">Clients Affected</p>
            </div>
          </div>
        </div>
        <div class="column is-2">
          <div class="card" style="background-color: #082B3F;">
            <div class="card-content has-text-centered">
              <p class="title is-4 has-text-white">{{ summaryStats.departments_affected }}</p>
              <p class="subtitle is-6 has-text-white">Departments</p>
            </div>
          </div>
        </div>
        <div class="column is-2">
          <div class="card" style="background-color: #364b5c;">
            <div class="card-content has-text-centered">
              <p class="title is-4 has-text-white">{{ summaryStats.success_rate_percentage }}%</p>
              <p class="subtitle is-6 has-text-white">Success Rate</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Overview Tables -->
      <div class="columns">
        <!-- Error Reason Message Overview -->
        <div class="column is-6">
          <div class="box">
            <h3 class="title is-6 mb-4">
              <span class="icon-text">
                <span class="icon">
                  <i class="fas fa-exclamation-triangle"></i>
                </span>
                <span>Error Reason Messages</span>
              </span>
            </h3>
            <div class="table-container">
              <table class="table is-striped is-hoverable is-fullwidth is-size-7">
                <thead>
                  <tr style="background-color: #0a1821; color: white;">
                    <th class="has-text-white">Error Message</th>
                    <th class="has-text-white has-text-centered">Count</th>
                    <th class="has-text-white has-text-centered">Percentage</th>
                    <th class="has-text-white has-text-centered">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(error, index) in errorOverview.slice(0, 10)" :key="index">
                    <td class="error-message-cell">
                      <span 
                        :title="error.error_message"
                        class="error-message-text"
                        @mouseenter="showTooltip = index"
                        @mouseleave="showTooltip = null"
                      >
                        {{ truncateText(error.error_message, 50) }}
                      </span>
                      <!-- Tooltip for full error message -->
                      <div 
                        v-if="showTooltip === index && error.error_message.length > 50"
                        class="error-tooltip"
                      >
                        {{ error.error_message }}
                      </div>
                    </td>
                    <td class="has-text-centered">
                      <span class="tag is-light">{{ formatNumber(error.count) }}</span>
                    </td>
                    <td class="has-text-centered">
                      <span class="tag is-light" style="background-color: #0a3954; color: white;">
                        {{ error.percentage.toFixed(1) }}%
                      </span>
                    </td>
                    <td class="has-text-centered">
                      <button 
                        class="button is-small is-outlined"
                        style="border-color: #0a3954; color: #0a3954;"
                        @click="viewErrorDetails(error.error_message)"
                        title="View detailed invoices"
                      >
                        <span class="icon is-small">
                          <i class="fas fa-eye"></i>
                        </span>
                        <span>Details</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="errorOverview.length > 10" class="has-text-grey is-size-7 mt-2">
              Showing top 10 of {{ errorOverview.length }} error types
            </p>
          </div>
        </div>

        <!-- Client Error Overview -->
        <div class="column is-6">
          <div class="box">
            <h3 class="title is-6 mb-4">
              <span class="icon-text">
                <span class="icon">
                  <i class="fas fa-users"></i>
                </span>
                <span>Client Error Analysis</span>
              </span>
            </h3>
            <div class="table-container">
              <table class="table is-striped is-hoverable is-fullwidth is-size-7">
                <thead>
                  <tr style="background-color: #0a1821; color: white;">
                    <th class="has-text-white">Client Name</th>
                    <th class="has-text-white has-text-centered">Entity Code</th>
                    <th class="has-text-white has-text-centered">Error Count</th>
                    <th class="has-text-white has-text-centered">Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(client, index) in clientOverview.slice(0, 10)" :key="index">
                    <td>
                      <span :title="client.client_name">
                        {{ truncateText(client.client_name, 25) }}
                      </span>
                    </td>
                    <td class="has-text-centered">
                      <span class="tag is-light">{{ client.entity_code }}</span>
                    </td>
                    <td class="has-text-centered">
                      <span class="tag" style="background-color: #ffc107; color: white;">
                        {{ formatNumber(client.error_count) }}
                      </span>
                    </td>
                    <td class="has-text-centered">
                      <span class="tag is-danger is-light">{{ client.percentage.toFixed(1) }}%</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="clientOverview.length > 10" class="has-text-grey is-size-7 mt-2">
              Showing top 10 of {{ clientOverview.length }} affected clients
            </p>
          </div>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="columns">
        <!-- Error Ranking Chart -->
        <div class="column is-6">
          <div class="box">
            <h3 class="title is-6 mb-4">
              <span class="icon-text">
                <span class="icon">
                  <i class="fas fa-chart-bar"></i>
                </span>
                <span>Error Frequency Ranking</span>
              </span>
            </h3>
            <error-ranking-chart 
              :data="errorRankingData" 
              :key="`error-chart-${chartKey}`"
            />
          </div>
        </div>

        <!-- Client Ranking Chart -->
        <div class="column is-6">
          <div class="box">
            <h3 class="title is-6 mb-4">
              <span class="icon-text">
                <span class="icon">
                  <i class="fas fa-chart-pie"></i>
                </span>
                <span>Client Error Distribution</span>
              </span>
            </h3>
            <client-ranking-chart 
              :data="clientRankingData" 
              :key="`client-chart-${chartKey}`"
            />
          </div>
        </div>
      </div>

      <!-- Department-Level Analysis -->
      <div class="box">
        <h3 class="title is-6 mb-4">
          <span class="icon-text">
            <span class="icon">
              <i class="fas fa-building"></i>
            </span>
            <span>Department-Level Analysis</span>
          </span>
        </h3>
        
        <div class="columns is-multiline">
          <div 
            v-for="department in departmentAnalysis" 
            :key="department.department"
            class="column is-6"
          >
            <div class="card">
              <header class="card-header" style="background-color: #0a1821;">
                <p class="card-header-title has-text-white">
                  Department: {{ department.department }}
                  <span class="tag is-danger is-light ml-2">
                    {{ formatNumber(department.total_errors) }} errors
                  </span>
                </p>
              </header>
              <div class="card-content">
                <div class="content">
                  <div class="columns is-mobile mb-3">
                    <div class="column">
                      <div class="has-text-centered">
                        <p class="title is-6">{{ department.unique_error_types }}</p>
                        <p class="subtitle is-7">Error Types</p>
                      </div>
                    </div>
                    <div class="column">
                      <div class="has-text-centered">
                        <p class="title is-6">{{ department.unique_clients_affected }}</p>
                        <p class="subtitle is-7">Clients Affected</p>
                      </div>
                    </div>
                  </div>

                  <!-- Top Errors in Department -->
                  <h4 class="subtitle is-6 mb-2">Top Errors:</h4>
                  <div class="mb-3">
                    <div 
                      v-for="(error, index) in department.top_errors.slice(0, 3)" 
                      :key="index"
                      class="level is-mobile mb-1"
                    >
                      <div class="level-left">
                        <div class="level-item">
                          <span class="tag is-small" style="background-color: #0a3954; color: white;">
                            {{ error.count }}
                          </span>
                        </div>
                        <div class="level-item">
                          <span class="is-size-7">
                            {{ truncateText(error.error_message, 35) }}
                          </span>
                        </div>
                      </div>
                      <div class="level-right">
                        <div class="level-item">
                          <span class="tag is-small is-light">
                            {{ error.percentage.toFixed(1) }}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Top Clients in Department -->
                  <h4 class="subtitle is-6 mb-2">Most Affected Clients:</h4>
                  <div>
                    <div 
                      v-for="(client, index) in department.top_clients.slice(0, 3)" 
                      :key="index"
                      class="level is-mobile mb-1"
                    >
                      <div class="level-left">
                        <div class="level-item">
                          <span class="tag is-small" style="background-color: #ffc107; color: white;">
                            {{ client.count }}
                          </span>
                        </div>
                        <div class="level-item">
                          <span class="is-size-7">
                            {{ truncateText(client.client_name, 20) }}
                          </span>
                        </div>
                      </div>
                      <div class="level-right">
                        <div class="level-item">
                          <span class="tag is-small is-light">
                            {{ client.percentage.toFixed(1) }}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Details Modal -->
    <div v-if="showErrorDetailsModal" class="modal is-active">
      <div class="modal-background" @click="closeErrorDetailsModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Error Details</p>
          <button class="delete" aria-label="close" @click="closeErrorDetailsModal"></button>
        </header>
        <section class="modal-card-body">
          <div v-if="errorDetailsLoading" class="notification is-info is-light">
            <span class="icon">
              <i class="fas fa-spinner fa-spin"></i>
            </span>
            <span>Loading error details...</span>
          </div>
          <div v-else>
            <h4 class="subtitle is-5 mb-3">Error Message:</h4>
            <div class="notification is-danger is-light mb-4">
              {{ selectedErrorMessage }}
            </div>
            
            <h4 class="subtitle is-5 mb-3">
              Affected Invoices ({{ errorDetails.total_matching_invoices }} total):
            </h4>
            
            <div class="table-container">
              <table class="table is-striped is-hoverable is-fullwidth">
                <thead>
                  <tr>
                    <th>Invoice Number</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Department</th>
                    <th>Failed At</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="invoice in errorDetails.invoice_details" :key="invoice.invoice_number">
                    <td>{{ invoice.invoice_number }}</td>
                    <td>{{ formatDate(invoice.invoice_date) }}</td>
                    <td>{{ truncateText(invoice.customer_name, 30) }}</td>
                    <td>
                      <span class="tag is-info is-light">{{ invoice.department }}</span>
                    </td>
                    <td>{{ formatDateTime(invoice.failed_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button" @click="closeErrorDetailsModal">Close</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ErrorRankingChart from './ErrorRankingChart.vue';
import ClientRankingChart from './ClientRankingChart.vue';

export default {
  name: 'ErrorAnalysisContent',
  components: {
    ErrorRankingChart,
    ClientRankingChart
  },
  props: {
    filters: {
      type: Object,
      default: () => ({})
    },
    dataVersion: {
      type: Number,
      default: 0
    },
    isActive: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoading: false,
      showTooltip: null,
      chartKey: 0, // Force chart re-rendering
      errorOverview: [],
      clientOverview: [],
      errorRankingData: [],
      clientRankingData: [],
      departmentAnalysis: [],
      summaryStats: {
        total_invoices: 0,
        total_failed_invoices: 0,
        failure_rate_percentage: 0,
        unique_error_types: 0,
        unique_clients_affected: 0,
        departments_affected: 0,
        success_rate_percentage: 0
      },
      
      // Error details modal
      showErrorDetailsModal: false,
      errorDetailsLoading: false,
      selectedErrorMessage: '',
      errorDetails: {
        total_matching_invoices: 0,
        invoice_details: []
      }
    };
  },
  watch: {
    dataVersion() {
      if (this.isActive) {
        this.fetchErrorAnalysisData();
      }
    },
    filters: {
      handler() {
        if (this.isActive) {
          this.fetchErrorAnalysisData();
        }
      },
      deep: true
    },
    isActive(newVal) {
      if (newVal && (!this.errorOverview.length && !this.isLoading)) {
        this.fetchErrorAnalysisData();
      }
    }
  },
  mounted() {
    // Only fetch data if the tab is active on mount
    if (this.isActive) {
      this.fetchErrorAnalysisData();
    }
  },
  methods: {
    async fetchErrorAnalysisData() {
      this.isLoading = true;
      try {
        const params = this.buildFilterParams();
        const response = await axios.get(`/api/v1/dashboard/error-analysis/?${params}`);

        const data = response.data;
        
        // Update all data
        this.errorOverview = data.error_overview || [];
        this.clientOverview = data.client_overview || [];
        this.errorRankingData = data.error_ranking || [];
        this.clientRankingData = data.client_ranking || [];
        this.departmentAnalysis = data.department_analysis || [];
        this.summaryStats = data.summary_statistics || this.summaryStats;
        
        // Force chart re-rendering
        this.chartKey++;
        
        console.log('Error analysis data fetched:', data);
        console.log('Error ranking data for chart:', this.errorRankingData);
        console.log('Client ranking data for chart:', this.clientRankingData);
        console.log('Chart key updated to:', this.chartKey);
      } catch (error) {
        console.error('Error fetching error analysis data:', error);
        this.$toast && this.$toast.error('Failed to load error analysis data');
      } finally {
        this.isLoading = false;
      }
    },
    
    buildFilterParams() {
      const params = new URLSearchParams();
      
      if (this.filters.start_date) {
        params.append('start_date', this.filters.start_date);
      }
      if (this.filters.end_date) {
        params.append('end_date', this.filters.end_date);
      }
      if (this.filters.department_codes && this.filters.department_codes.length > 0) {
        this.filters.department_codes.forEach(dept => {
          params.append('department_codes', dept.code || dept);
        });
      }
      if (this.filters.status && this.filters.status.length > 0) {
        this.filters.status.forEach(status => {
          params.append('status', status);
        });
      }
      if (this.filters.invoice_types && this.filters.invoice_types.length > 0) {
        this.filters.invoice_types.forEach(type => {
          params.append('invoice_types', type.code || type);
        });
      }
      if (this.filters.source_systems && this.filters.source_systems.length > 0) {
        this.filters.source_systems.forEach(system => {
          params.append('source_systems', system.value || system);
        });
      }
      
      return params;
    },
    
    async viewErrorDetails(errorMessage) {
      this.selectedErrorMessage = errorMessage;
      this.showErrorDetailsModal = true;
      this.errorDetailsLoading = true;
      
      try {
        const params = new URLSearchParams({
          error_message: errorMessage
        });
        
        // Add department filter if selected
        if (this.filters.department_codes && this.filters.department_codes.length === 1) {
          params.append('department', this.filters.department_codes[0].code || this.filters.department_codes[0]);
        }
        
        const response = await axios.get(`/api/v1/dashboard/error-details/?${params}`);
        this.errorDetails = response.data;
      } catch (error) {
        console.error('Error fetching error details:', error);
        this.$toast && this.$toast.error('Failed to load error details');
      } finally {
        this.errorDetailsLoading = false;
      }
    },
    
    closeErrorDetailsModal() {
      this.showErrorDetailsModal = false;
      this.selectedErrorMessage = '';
      this.errorDetails = {
        total_matching_invoices: 0,
        invoice_details: []
      };
    },
    
    formatNumber(value) {
      return value ? value.toLocaleString() : '0';
    },
    
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString();
    },
    
    formatDateTime(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString();
    },
    
    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    }
  }
};
</script>

<style scoped>
.error-analysis-content {
  padding: 0;
}

.error-message-cell {
  max-width: 300px;
  position: relative;
}

.error-message-text {
  word-break: break-word;
  line-height: 1.4;
  cursor: help;
}

.error-tooltip {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background-color: #363636;
  color: white;
  padding: 0.5rem;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  font-size: 0.75rem;
  line-height: 1.3;
  max-width: 400px;
  word-wrap: break-word;
  white-space: normal;
}

.table-container {
  max-height: 400px;
  overflow-y: auto;
}

.table th {
  vertical-align: middle;
  text-align: center;
  padding: 0.75rem 0.5rem;
  border-color: #0a3954;
}

.table td {
  padding: 0.75rem 0.5rem;
  vertical-align: middle;
}

.table tbody tr:hover {
  background-color: #f8f9fa;
}

.card-content {
  padding: 1rem;
}

.level.is-mobile {
  margin-bottom: 0.5rem;
}

.level.is-mobile .level-left,
.level.is-mobile .level-right {
  margin-bottom: 0;
}

.modal-card {
  width: 90vw;
  max-width: 1200px;
}

.modal-card-body {
  max-height: 70vh;
  overflow-y: auto;
}

/* Summary cards styling */
.card {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.card-content {
  padding: 1.5rem;
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .columns {
    margin: 0;
  }
  
  .column {
    padding: 0.5rem;
  }
  
  .error-message-cell {
    max-width: 200px;
  }
  
  .modal-card {
    width: 95vw;
    margin: 1rem;
  }
  
  .error-tooltip {
    max-width: 250px;
    font-size: 0.7rem;
  }
  
  .table {
    font-size: 0.75rem;
  }
  
  .table th,
  .table td {
    padding: 0.5rem 0.25rem;
  }
}

/* Make sure long numbers don't break layout */
.table td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.table th {
  white-space: nowrap;
}

/* Ensure consistent table styling */
.table.is-size-7 th,
.table.is-size-7 td {
  font-size: 0.75rem;
}
</style>
